import React, { createContext, useContext, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { authFetch } from '../common/authFetch';

const VocabularyContext = createContext();

const API_URL = process.env.REACT_APP_BACKEND_URL;

// 관찰 대상이 선택된 경우 ?userId= 를 붙인다
const withUser = (params, targetUserId) => {
  if (targetUserId) params.append('userId', targetUserId);
  return params;
};

export const VocabularyProvider = ({ children }) => {
  const { t } = useTranslation();
  /*
   * 관찰 대상 학생. 화면마다 따로 들고 있으면 시험 → 응시/시험지 화면으로 넘어갈 때
   * 선택이 사라져 남의 시험을 조회하지 못한다. 전역으로 두고 모든 호출의 기본값으로 쓴다.
   */
  const [studentId, setStudentId] = useState('');
  const [dates, setDates] = useState([]);
  const [words, setWords] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, total_pages: 1, total: 0, limit: 50, sort: 'seq', order: 'asc' });
  const [loading, setLoading] = useState(false);

  const fetchDates = useCallback(async (targetUserId) => {
    try {
      setLoading(true);
      const params = withUser(new URLSearchParams(), targetUserId);
      const res = await authFetch(`${API_URL}/api/v1/vocabulary/dates?${params}`);
      const data = await res.json();
      if (data.success) {
        setDates(data.dates || []);
        return data.dates || [];
      }
      return [];
    } catch (error) {
      console.error('Error fetching vocabulary dates:', error);
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchWords = useCallback(async ({ date, important, q, page = 1, limit, sort, order, targetUserId } = {}) => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (date) params.append('date', date);
      if (important) params.append('important', '1');
      if (q) params.append('q', q);
      if (sort) params.append('sort', sort);
      if (order) params.append('order', order);
      if (limit) params.append('limit', String(limit));
      params.append('page', String(page));
      withUser(params, targetUserId);

      const res = await authFetch(`${API_URL}/api/v1/vocabulary/words?${params}`);
      const data = await res.json();
      if (data.success) {
        setWords(data.words || []);
        setPagination({
          page: data.page || 1,
          total_pages: data.total_pages || 1,
          total: data.total || 0,
          limit: data.limit || 100,
          sort: data.sort || 'seq',
          order: data.order || 'asc',
          grouped_by_date: !!data.grouped_by_date,
        });
      }
    } catch (error) {
      console.error('Error fetching vocabulary words:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * CSV 업로드. 성공 시 집계 결과 객체를 반환한다.
   */
  /** 날짜에 붙인 단어장 이름 수정. 비우면 날짜로 되돌아간다 */
  const updateDateAlias = useCallback(async (studyDate, alias, targetUserId) => {
    try {
      const params = withUser(new URLSearchParams(), targetUserId);
      const res = await authFetch(`${API_URL}/api/v1/vocabulary/dates/${studyDate}/alias?${params}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ alias }),
      });
      const data = await res.json();
      if (data.success) return data.alias;
      toast.error(t('error.save'));
      return null;
    } catch (error) {
      console.error('Error updating alias:', error);
      toast.error(t('error.save'));
      return null;
    }
  }, [t]);

  const importCsv = useCallback(async (file, studyDate, targetUserId, alias) => {
    try {
      setLoading(true);
      const form = new FormData();
      form.append('file', file);
      form.append('study_date', studyDate);
      // 단어장 이름. 비우면 서버가 날짜를 그대로 쓴다
      if (alias) form.append('alias', alias);
      if (targetUserId) form.append('userId', String(targetUserId));

      const params = withUser(new URLSearchParams(), targetUserId);
      const res = await authFetch(`${API_URL}/api/v1/vocabulary/import?${params}`, {
        method: 'POST',
        body: form,
      });
      const data = await res.json();

      if (data.success) {
        toast.success(t('vocabulary.uploadSuccess'));
        return data;
      }
      toast.error(t('vocabulary.uploadFailed'));
      return null;
    } catch (error) {
      console.error('Error importing CSV:', error);
      toast.error(t('vocabulary.uploadFailed'));
      return null;
    } finally {
      setLoading(false);
    }
  }, [t]);

  const createWord = useCallback(async (payload, targetUserId) => {
    try {
      const params = withUser(new URLSearchParams(), targetUserId);
      const res = await authFetch(`${API_URL}/api/v1/vocabulary/words?${params}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (data.success) return true;
      toast.error(t('error.save'));
      return false;
    } catch (error) {
      console.error('Error creating word:', error);
      toast.error(t('error.save'));
      return false;
    }
  }, [t]);

  const updateWord = useCallback(async (wordId, payload, targetUserId) => {
    try {
      const params = withUser(new URLSearchParams(), targetUserId);
      const res = await authFetch(`${API_URL}/api/v1/vocabulary/words/${wordId}?${params}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (data.success) return true;
      toast.error(t('error.save'));
      return false;
    } catch (error) {
      console.error('Error updating word:', error);
      toast.error(t('error.save'));
      return false;
    }
  }, [t]);

  /**
   * 선택한 단어 여러 개를 한 번에 삭제한다.
   * @returns {number|null} 실제 삭제된 개수, 실패 시 null
   */
  const deleteWords = useCallback(async (wordIds, targetUserId) => {
    try {
      const params = withUser(new URLSearchParams(), targetUserId);
      const res = await authFetch(`${API_URL}/api/v1/vocabulary/words?${params}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ word_ids: wordIds }),
      });
      const data = await res.json();
      if (data.success) {
        toast.success(t('vocabulary.deletedCount', { count: data.deleted }));
        return data.deleted;
      }
      toast.error(t('error.save'));
      return null;
    } catch (error) {
      console.error('Error deleting words:', error);
      toast.error(t('error.save'));
      return null;
    }
  }, [t]);

  const deleteWord = useCallback(async (wordId, targetUserId) => {
    try {
      const params = withUser(new URLSearchParams(), targetUserId);
      const res = await authFetch(`${API_URL}/api/v1/vocabulary/words/${wordId}?${params}`, {
        method: 'DELETE',
      });
      const data = await res.json();
      if (data.success) {
        toast.success(t('deleted'));
        return true;
      }
      toast.error(t('error.save'));
      return false;
    } catch (error) {
      console.error('Error deleting word:', error);
      toast.error(t('error.save'));
      return false;
    }
  }, [t]);

  // ── 시험 · 오답 노트 ──────────────────────────────────────
  // 화면마다 필요한 데이터가 달라 전역 상태로 두지 않고 호출 함수만 제공한다

  const request = useCallback(async (path, { method = 'GET', body, targetUserId, params } = {}) => {
    // 호출부가 명시하지 않으면 선택된 관찰 대상을 쓴다
    const search = withUser(new URLSearchParams(params || {}), targetUserId ?? (studentId || undefined));
    const options = { method };
    if (body !== undefined) {
      options.headers = { 'Content-Type': 'application/json' };
      options.body = JSON.stringify(body);
    }
    const res = await authFetch(`${API_URL}/api/v1/vocabulary/${path}?${search}`, options);
    return res.json();
  }, [studentId]);

  const wordbookApi = {
    /** 고른 날짜의 단어를 발음기호까지 붙여 한 권으로 저장한다 */
    createWordbook: (payload, targetUserId) =>
      request('wordbooks', { method: 'POST', body: payload, targetUserId }),

    listWordbooks: (params, targetUserId) => request('wordbooks', { params, targetUserId }),
    getWordbook: (wordbookId, targetUserId) => request(`wordbooks/${wordbookId}`, { targetUserId }),

    /** 제목 변경 */
    updateWordbook: (wordbookId, payload, targetUserId) =>
      request(`wordbooks/${wordbookId}`, { method: 'PUT', body: payload, targetUserId }),

    deleteWordbook: (wordbookId, targetUserId) =>
      request(`wordbooks/${wordbookId}`, { method: 'DELETE', targetUserId }),
  };

  const quizApi = {
    createQuiz: (payload, targetUserId) =>
      request('quizzes', { method: 'POST', body: payload, targetUserId }),

    /** 기간을 정해 그 기간에 배운 단어 중 틀린 것만 모아 시험을 만든다 */
    createReviewQuiz: (payload, targetUserId) =>
      request('quizzes/review', { method: 'POST', body: payload, targetUserId }),

    /** 선택 범위에서 실제로 출제 가능한 문항 수 (중복 제거 후) */
    getPoolSize: (params, targetUserId) => request('quizzes/pool', { params, targetUserId }),

    listQuizzes: (params, targetUserId) => request('quizzes', { params, targetUserId }),
    getQuiz: (quizId, targetUserId) => request(`quizzes/${quizId}`, { targetUserId }),

    /** 시험지 PDF 출력용 문항 전체. includeAnswers면 정답지까지 받는다 */
    getQuizPaper: (quizId, includeAnswers, targetUserId) =>
      request(`quizzes/${quizId}/paper`, {
        params: includeAnswers ? { answers: '1' } : undefined,
        targetUserId,
      }),

    startQuiz: (quizId, targetUserId) =>
      request(`quizzes/${quizId}/start`, { method: 'POST', body: {}, targetUserId }),
    submitAnswer: (quizId, payload, targetUserId) =>
      request(`quizzes/${quizId}/answer`, { method: 'POST', body: payload, targetUserId }),

    /** 종이로 푼 답안을 한 번에 보내 채점한다 */
    submitAnswers: (quizId, payload, targetUserId) =>
      request(`quizzes/${quizId}/answers`, { method: 'POST', body: payload, targetUserId }),

    finishQuiz: (quizId, targetUserId) =>
      request(`quizzes/${quizId}/finish`, { method: 'POST', body: {}, targetUserId }),
    getResult: (quizId, targetUserId) => request(`quizzes/${quizId}/result`, { targetUserId }),
    /** 시험 제목 변경 */
    updateQuiz: (quizId, payload, targetUserId) =>
      request(`quizzes/${quizId}`, { method: 'PUT', body: payload, targetUserId }),

    deleteQuiz: (quizId, targetUserId) =>
      request(`quizzes/${quizId}`, { method: 'DELETE', targetUserId }),

    getWrongNotes: (params, targetUserId) => request('wrong-notes', { params, targetUserId }),
    getWeakness: (params, targetUserId) => request('weakness', { params, targetUserId }),
    getTrend: (params, targetUserId) => request('trend', { params, targetUserId }),

    /** 오답 AI 분석 — 약점과 학습 방향 (유료 호출이라 사용자가 눌러야 실행된다) */
    analyzeWrongAnswers: (payload, targetUserId) =>
      request('analysis', { method: 'POST', body: payload, targetUserId }),

    /** 저장된 분석 이력 — 기간별로 쌓인 것을 다시 본다 */
    listAnalyses: (params, targetUserId) => request('analyses', { params, targetUserId }),
    getAnalysis: (analysisId, targetUserId) => request(`analyses/${analysisId}`, { targetUserId }),
    deleteAnalysis: (analysisId, targetUserId) =>
      request(`analyses/${analysisId}`, { method: 'DELETE', targetUserId }),
  };

  const value = {
    studentId,
    setStudentId,
    dates,
    words,
    pagination,
    loading,
    quizApi,
    wordbookApi,
    fetchDates,
    fetchWords,
    importCsv,
    updateDateAlias,
    createWord,
    updateWord,
    deleteWord,
    deleteWords,
  };

  return (
    <VocabularyContext.Provider value={value}>
      {children}
    </VocabularyContext.Provider>
  );
};

export const useVocabulary = () => {
  const context = useContext(VocabularyContext);
  if (!context) {
    throw new Error('useVocabulary must be used within a VocabularyProvider');
  }
  return context;
};
